import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useState, useEffect } from "react";
import { SchemaDefinition, FieldDefinition } from "./types";

type RecordEditDialogProps<T extends { id: string }> = {
  open: boolean;
  onClose: () => void;
  onSave: (record: T) => void;
  record: T | null;
  schemaDefinition: SchemaDefinition<T>;
  title?: string;
};

const renderBooleanField = (
  field: FieldDefinition,
  value: any,
  handleChange: (fieldName: string, value: any) => void
) => (
  <FormControlLabel
    key={field.name}
    control={
      <Checkbox
        checked={Boolean(value)}
        onChange={(e) => handleChange(field.name, e.target.checked)}
      />
    }
    label={field.label}
  />
);

const renderEnumField = (
  field: FieldDefinition,
  value: any,
  handleChange: (fieldName: string, value: any) => void
) => (
  <FormControl key={field.name} fullWidth>
    <InputLabel>{field.label}</InputLabel>
    <Select
      value={value}
      onChange={(e) => handleChange(field.name, e.target.value)}
      label={field.label}
    >
      {field.enumValues?.map((enumValue) => (
        <MenuItem key={enumValue} value={enumValue}>
          {enumValue}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const renderSelectField = (
  field: FieldDefinition,
  value: any,
  handleChange: (fieldName: string, value: any) => void
) => (
  <FormControl key={field.name} fullWidth>
    <InputLabel>{field.label}</InputLabel>
    <Select
      value={value || ""}
      onChange={(e) => handleChange(field.name, e.target.value || undefined)}
      label={field.label}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {field.selectOptions?.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const renderNumberField = (
  field: FieldDefinition,
  formData: Record<string, any>,
  fieldErrors: Record<string, string>,
  numberDrafts: Record<string, string>,
  handleNumberChange: (field: FieldDefinition, raw: string) => void
) => {
  const rawNum = formData[field.name];
  const fieldError = fieldErrors[field.name];
  const draft = numberDrafts[field.name];
  const displayNum =
    fieldError !== undefined && draft !== undefined
      ? draft
      : rawNum === null || rawNum === undefined || rawNum === ""
        ? ""
        : rawNum;
  return (
    <TextField
      key={field.name}
      fullWidth
      label={field.label}
      type="number"
      value={displayNum}
      inputProps={
        field.numberMin !== undefined ? { min: field.numberMin, step: 1 } : undefined
      }
      onChange={(e) => handleNumberChange(field, e.target.value)}
      variant="outlined"
      required={field.required}
      error={fieldError !== undefined}
      helperText={fieldError ?? field.editHelperText}
    />
  );
};

const renderStringLikeField = <T extends { id: string }>(
  field: FieldDefinition,
  value: any,
  record: T | null,
  handleChange: (fieldName: string, value: any) => void
) => {
  const isPassword = field.inputType === "password";
  const hasExistingValue =
    isPassword && record != null && Boolean(record[field.name as keyof T]);
  return (
    <TextField
      key={field.name}
      fullWidth
      label={field.label}
      type={isPassword ? "password" : "text"}
      value={value ?? ""}
      onChange={(e) => handleChange(field.name, e.target.value)}
      variant="outlined"
      required={field.required}
      helperText={
        hasExistingValue ? "Leave blank to keep the existing value" : undefined
      }
    />
  );
};

const renderFieldInner = <T extends { id: string }>(
  field: FieldDefinition,
  formData: Record<string, any>,
  record: T | null,
  fieldErrors: Record<string, string>,
  numberDrafts: Record<string, string>,
  handleChange: (fieldName: string, value: any) => void,
  handleNumberChange: (field: FieldDefinition, raw: string) => void
) => {
  const value = formData[field.name] ?? "";
  switch (field.type) {
    case "boolean":
      return renderBooleanField(field, value, handleChange);
    case "enum":
      return renderEnumField(field, value, handleChange);
    case "select":
      return renderSelectField(field, value, handleChange);
    case "number":
      return renderNumberField(field, formData, fieldErrors, numberDrafts, handleNumberChange);
    case "string":
    default:
      return renderStringLikeField(field, value, record, handleChange);
  }
};

export const RecordEditDialog = <T extends { id: string }>({
  open,
  onClose,
  onSave,
  record,
  schemaDefinition,
  title,
}: RecordEditDialogProps<T>) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [numberDrafts, setNumberDrafts] = useState<Record<string, string>>({});

  // Initialize form data when record changes
  useEffect(() => {
    if (record) {
      const data: Record<string, any> = {};
      schemaDefinition.fields.forEach((field) => {
        data[field.name] =
          field.inputType === "password" ? "" : record[field.name as keyof T];
      });
      setFormData(data);
      setFieldErrors({});
      setNumberDrafts({});
    }
  }, [record, schemaDefinition.fields]);

  const handleChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleNumberChange = (field: FieldDefinition, raw: string) => {
    if (field.valueParser) {
      setNumberDrafts((prev) => ({ ...prev, [field.name]: raw }));
      try {
        const parsed = field.valueParser(raw);
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field.name];
          return next;
        });
        setNumberDrafts((prev) => {
          const next = { ...prev };
          delete next[field.name];
          return next;
        });
        handleChange(field.name, parsed);
      } catch (e) {
        setFieldErrors((prev) => ({
          ...prev,
          [field.name]: e instanceof Error ? e.message : String(e),
        }));
      }
      return;
    }
    handleChange(field.name, parseFloat(raw) || 0);
  };

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const handleSave = () => {
    if (hasFieldErrors) return;
    if (record) {
      const cleanedFormData = { ...formData };
      schemaDefinition.fields.forEach((field) => {
        if (field.inputType === "password" && cleanedFormData[field.name] === "") {
          delete cleanedFormData[field.name];
        }
      });
      onSave({
        ...record,
        ...cleanedFormData,
      } as T);
    }
    onClose();
  };

  const handleClose = () => {
    setFormData({});
    setFieldErrors({});
    setNumberDrafts({});
    onClose();
  };

  const renderField = (field: FieldDefinition) =>
    renderFieldInner(field, formData, record, fieldErrors, numberDrafts, handleChange, handleNumberChange);

  const defaultTitle = title || `Edit ${schemaDefinition.entityName || "Record"}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{defaultTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {schemaDefinition.fields.map(renderField)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={hasFieldErrors}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
